<template>
  <div class="relative w-full">
    <!-- Nápověda kroku -->
    <div v-if="!cart" class="text-xs text-gray-500 mb-2">
      <span v-if="isComplete" class="text-green-600 font-semibold">
        <i class="fas fa-check mr-1"></i>{{ t('marketplace.calendar.period_selected', 'Period selected') }}
      </span>
      <span v-else class="text-gray-700">
        <span class="font-semibold">{{ stepLabel }}</span>
        ·
        {{ stepHint }}
      </span>
    </div>

    <!-- 2×2 mřížka polí (lekce: 1×2) -->
    <div class="grid grid-cols-2 gap-2">
      <div v-for="(field, index) in fields" :key="field" class="relative">
        <!-- Pole -->
        <button type="button"
                class="w-full text-left rounded-lg py-3 px-4 border-2 transition-all flex items-center gap-2"
                :class="fieldClasses(field)"
                :disabled="isLocked(field) || !!cart"
                @click="toggleField(field)">
          <span class="grow min-w-0">
            <span class="block text-xs" :class="labelClasses(field)">{{ fieldLabel(field) }}</span>
            <span class="block font-semibold text-base truncate" :class="valueClasses(field)">
              {{ fieldValue(field) || t('marketplace.calendar.select', 'Select') }}
            </span>
          </span>
          <i v-if="fieldValue(field) && activeField !== field" class="fas fa-check text-green-600 shrink-0"></i>
          <i v-else-if="activeField === field" class="fas fa-chevron-up text-blue-600 shrink-0"></i>
          <i v-else-if="isCurrent(field)" class="fas fa-chevron-down shrink-0"></i>
          <i v-else-if="isLocked(field) && !cart" class="fas fa-lock text-gray-400 shrink-0"></i>
        </button>

        <!-- Popover -->
        <div v-if="activeField === field && !cart"
             class="absolute top-0 z-50 bg-white rounded-lg shadow-2xl border p-3 w-full min-w-[320px]"
             :class="index % 2 === 0 ? 'left-0' : 'right-0'">
          <button type="button"
                  class="w-full text-left rounded-lg py-2 px-3 mb-3 border-2 border-black bg-white"
                  @click="closeAll">
            <span class="block text-xs text-gray-500">{{ fieldLabel(field) }}</span>
            <span class="block font-semibold text-base">{{ fieldValue(field) || '—' }}</span>
          </button>

          <!-- Kalendář -->
          <template v-if="field === 'startDate'">
            <DatePicker ref="startCalendar" is-expanded is-required :locale="calendarLocale"
                        v-model="startDateProxy"
                        :disabled-dates="disabledDates" :attributes="attributes"
                        @input="onStartDatePicked" @dayclick="onStartDayClick" @update:from-page="onCalendarPageChange" />
          </template>
          <template v-else-if="field === 'endDate'">
            <DatePicker ref="endCalendar" is-expanded is-required :locale="calendarLocale"
                        v-model="endDateProxy"
                        :disabled-dates="endDisabledDates" :attributes="attributes"
                        @input="onEndDatePicked" @dayclick="onEndDayClick" @update:from-page="onCalendarPageChange" />
          </template>

          <!-- Mřížka časů -->
          <template v-else>
            <div class="max-h-64 overflow-y-auto pr-1">
              <template v-for="group in timeGroups(field)">
                <div v-if="group.slots.length" :key="group.key">
                  <div class="text-xs text-gray-500 mt-2 mb-1">{{ group.label }}</div>
                  <div class="grid grid-cols-2 gap-2 mb-1">
                    <button v-for="slot in group.slots" :key="slot.time" type="button"
                            class="rounded-lg py-2 font-semibold text-sm transition-colors"
                            :class="{
                              'bg-black text-white': selectedTime(field) === slot.time,
                              'bg-gray-100 hover:bg-gray-200': selectedTime(field) !== slot.time && slot.available,
                              'bg-gray-100 opacity-30 line-through cursor-not-allowed': !slot.available,
                            }"
                            :disabled="!slot.available"
                            @click="onTimePicked(field, slot.time)">
                      {{ slot.time }}
                    </button>
                  </div>
                </div>
              </template>
              <div v-if="!hasAnySlot(field)" class="text-sm text-red-500 py-2">
                {{ t('marketplace.calendar.no_slots', 'No available time slots') }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Chyba minimální délky (pojistka, sloty už jsou filtrované) -->
    <div v-if="fromTime && toTime && periodTooSmall" class="text-red-500 text-xs mt-2">
      {{ i18n.t('calendar.period_too_small', { hours: minimumRental }) }}
    </div>

    <!-- Backdrop: klik mimo zavře popover -->
    <div v-if="activeField" class="fixed inset-0 z-40" @click="closeAll"></div>
  </div>
</template>

<script>
  import DatePicker from 'v-calendar/lib/components/date-picker.umd'

  const SUPPORTED_LOCALES = ['en', 'cs', 'de', 'es', 'fr', 'it'];

  export default {
    name: 'ItemCalendar',
    components: { DatePicker },
    props: {
      cart: Object,
      availabilityUrl: String,
      minimumRental: Number,
      isLesson: { type: Boolean, default: false },
      // Marketplace předvyplňuje datum z URL (?start_date=&end_date=) nebo z košíku.
      // Online store je neposílá — pak se prostě začíná od prázdna.
      initialStartDate: { type: String, default: null },
      initialEndDate: { type: String, default: null },
    },
    data() {
      return {
        range: { start: null, end: null },
        fromTime: null,
        toTime: null,
        activeField: null,          // 'startDate' | 'startTime' | 'endDate' | 'endTime' | null
        startDateProxy: new Date(),
        endDateProxy: new Date(),
        availability: {},
        disabledDates: [],
        loadedMonths: new Set(),
        attributes: [
          { key: 'open', bar: 'green', popover: { label: this.i18n.t('calendar.full_availability'), placement: 'top' }, dates: [] },
          { key: 'closed', bar: 'red', popover: { label: this.i18n.t('calendar.shop_closed'), placement: 'top' }, dates: [] },
          { key: 'booked', popover: { label: this.i18n.t('calendar.all_booked'), placement: 'top' }, dates: [] },
          { key: 'partial', bar: 'yellow', popover: { label: this.i18n.t('calendar.partially_booked'), placement: 'top' }, dates: [] },
        ],
      };
    },
    computed: {
      fields() {
        return this.isLesson ? ['startDate', 'startTime'] : ['startDate', 'startTime', 'endDate', 'endTime'];
      },
      calendarLocale() {
        const l = (this.i18n && this.i18n.locale) ? String(this.i18n.locale).slice(0, 2) : 'en';
        return SUPPORTED_LOCALES.includes(l) ? l : 'en';
      },
      currentStepIndex() {
        for (let i = 0; i < this.fields.length; i++) {
          if (!this.fieldValue(this.fields[i])) return i;
        }
        return this.fields.length - 1;
      },
      isComplete() {
        return this.fields.every((f) => !!this.fieldValue(f));
      },
      stepLabel() {
        // Pozor: fallback obsahuje {{step}}/{{total}} — nesmí být přímo v šabloně,
        // Vue by si ty složené závorky vyložilo jako konec interpolace.
        return this.t('marketplace.calendar.step_of', 'Step {{step}} of {{total}}', {
          step: this.currentStepIndex + 1,
          total: this.fields.length,
        });
      },
      stepHint() {
        const f = this.fields[this.currentStepIndex];
        const map = {
          startDate: this.t('marketplace.calendar.hint_pickup_date', 'choose pick-up date'),
          startTime: this.t('marketplace.calendar.hint_pickup_time', 'choose pick-up time'),
          endDate:   this.t('marketplace.calendar.hint_return_date', 'choose return date'),
          endTime:   this.t('marketplace.calendar.hint_return_time', 'choose return time'),
        };
        return map[f];
      },
      startDateTime() {
        return this.range.start && this.fromTime ? this.setDayTime(this.range.start, this.fromTime) : null;
      },
      bookPeriod() {
        if (this.isLesson) {
          return { from: this.startDateTime ? `${this.getDate(this.range.start)} ${this.fromTime}:00` : null };
        }
        const complete = this.range.start && this.fromTime && this.range.end && this.toTime;
        return {
          from: complete ? `${this.getDate(this.range.start)} ${this.fromTime}:00` : null,
          to:   complete ? `${this.getDate(this.range.end)} ${this.toTime}:00` : null,
        };
      },
      periodTooSmall() {
        if (!this.bookPeriod.from || !this.bookPeriod.to || !this.minimumRental) return false;
        return this.diffHours(new Date(this.bookPeriod.from), new Date(this.bookPeriod.to)) < this.minimumRental;
      },
      validPeriod() {
        if (this.isLesson) return !!this.bookPeriod.from;
        return !!this.bookPeriod.from && !!this.bookPeriod.to && !this.periodTooSmall;
      },
      endDisabledDates() {
        if (!this.range.start) return this.disabledDates;
        const dayBefore = new Date(this.range.start.getTime() - 86400000);
        const list = [{ start: null, end: this.beginOfDay(dayBefore) }, ...this.disabledDates];
        // Za nejbližší další rezervací už vrátit nejde (věc by byla v tu dobu půjčená)
        if (this.nextBlockedAfterStart) {
          const blockedDay = this.beginOfDay(this.nextBlockedAfterStart);
          list.push({ start: new Date(blockedDay.getTime() + 86400000), end: null });
        }
        return list;
      },
      // První okamžik po vyzvednutí, kdy už věc není volná (jiná rezervace / plno).
      // Zavřené sloty a zavřené dny nevadí — věc je v tu dobu prostě u zákazníka.
      nextBlockedAfterStart() {
        if (this.isLesson || !this.startDateTime) return null;
        const start = this.startDateTime;
        const startDay = this.beginOfDay(start).getTime();
        const days = Object.keys(this.availability)
          .map((k) => ({ key: k, day: this.beginOfDay(new Date(k)) }))
          .filter((d) => d.day.getTime() >= startDay)
          .sort((a, b) => a.day - b.day);
        for (const d of days) {
          const v = this.availability[d.key];
          if (v === 'closed') continue;
          if (v === 'booked') {
            if (d.day.getTime() > startDay) return d.day;
            continue;
          }
          if (!v || typeof v !== 'object') continue;
          const times = Object.keys(v).sort((a, b) => this.minutes(a) - this.minutes(b));
          for (const time of times) {
            if (v[time] !== 'booked') continue;
            const at = this.setDayTime(d.day, time);
            if (at > start) return at;
          }
        }
        return null;
      },
    },
    watch: {
      bookPeriod() { this.emitPeriod(); },
    },
    mounted() {
      const now = new Date();
      this.getAvailability(now.getMonth() + 1, now.getFullYear());
      this.loadedMonths.add(`${now.getFullYear()}-${now.getMonth() + 1}`);

      // Předvyplnění z URL / košíku: nastavíme jen DATA, nikdy časy —
      // ty si musí zákazník vybrat vědomě.
      const start = this.parseIncoming(this.initialStartDate);
      if (!start) return;

      this.range.start = start;
      this.startDateProxy = start;

      if (!this.isLesson) {
        const end = this.parseIncoming(this.initialEndDate);
        if (end && end.getTime() >= start.getTime()) {
          this.range.end = end;
          this.endDateProxy = end;
        }
      }

      // Načteme i měsíc předvyplněného data, kdyby byl jiný než aktuální
      const key = `${start.getFullYear()}-${start.getMonth() + 1}`;
      if (!this.loadedMonths.has(key)) {
        this.loadedMonths.add(key);
        this.getAvailability(start.getMonth() + 1, start.getFullYear());
      }

      this.activeField = 'startTime';
    },
    methods: {
      // ---------- i18n s fallbackem (i18n-js vrací "[missing ...]" místo prázdna) ----------
      t(key, fallback, params) {
        const v = this.i18n.t(key, params || {});
        if (!v || String(v).indexOf('[missing') === 0) {
          return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) => (params && params[k] != null ? params[k] : ''));
        }
        return v;
      },

      // ---------- Pole ----------
      fieldLabel(field) {
        const map = {
          startDate: this.isLesson ? this.t('marketplace.calendar.start_date', 'Date') : this.t('marketplace.calendar.pick_up_date', 'Pick-up date'),
          startTime: this.isLesson ? this.i18n.t('marketplace.calendar.start_time') : this.i18n.t('marketplace.calendar.pick_up_time'),
          endDate:   this.t('marketplace.calendar.return_date', 'Return date'),
          endTime:   this.i18n.t('marketplace.calendar.return_time'),
        };
        return map[field];
      },
      fieldValue(field) {
        switch (field) {
          case 'startDate': return this.getDateWZero(this.range.start);
          case 'startTime': return this.fromTime;
          case 'endDate':   return this.getDateWZero(this.range.end);
          case 'endTime':   return this.toTime;
        }
        return null;
      },
      isLocked(field) {
        // Pole je zamčené, dokud není vyplněné každé předchozí
        const idx = this.fields.indexOf(field);
        for (let i = 0; i < idx; i++) {
          if (!this.fieldValue(this.fields[i])) return true;
        }
        return false;
      },
      // Pole, které je právě na řadě: odemčené, ještě prázdné a zavřené.
      // Dostává nejvýraznější vzhled, aby bylo jasné, kam kliknout.
      isCurrent(field) {
        return !this.cart
          && this.activeField !== field
          && !this.isLocked(field)
          && !this.fieldValue(field);
      },
      fieldClasses(field) {
        const locked = this.isLocked(field);
        const current = this.isCurrent(field);
        return {
          'bg-blue-50 border-blue-500': this.activeField === field,
          'bg-green-50 border-green-500': this.activeField !== field && !!this.fieldValue(field),
          'bg-white border-gray-900 shadow-md': current,
          'bg-gray-100 border-transparent': this.activeField !== field && !this.fieldValue(field) && !current,
          'opacity-60 cursor-not-allowed': locked,
          'cursor-pointer': !locked,
        };
      },
      labelClasses(field) {
        return {
          'text-blue-600': this.activeField === field,
          'text-green-600': this.activeField !== field && !!this.fieldValue(field),
          'text-gray-600': this.isCurrent(field),
          'text-gray-500': !this.isCurrent(field) && this.activeField !== field && !this.fieldValue(field),
        };
      },
      valueClasses(field) {
        if (this.fieldValue(field)) return {};
        return {
          'text-gray-900': this.isCurrent(field) || this.activeField === field,
          'text-gray-400 font-medium': !this.isCurrent(field) && this.activeField !== field,
        };
      },
      toggleField(field) {
        if (this.isLocked(field) || this.cart) return;
        if (this.activeField === field) { this.activeField = null; return; }
        if (field === 'startDate') this.startDateProxy = this.range.start || this.firstAvailableDate() || new Date();
        if (field === 'endDate')   this.endDateProxy = this.range.end || this.range.start || new Date();
        this.activeField = field;
      },
      closeAll() { this.activeField = null; },
      advanceFrom(field) {
        const idx = this.fields.indexOf(field);
        this.activeField = idx < this.fields.length - 1 ? this.fields[idx + 1] : null;
        if (this.activeField === 'endDate') this.endDateProxy = this.range.end || this.range.start;
      },
      resetAfter(field) {
        const idx = this.fields.indexOf(field);
        this.fields.slice(idx + 1).forEach((f) => {
          if (f === 'startTime') this.fromTime = null;
          if (f === 'endDate')   this.range.end = null;
          if (f === 'endTime')   this.toTime = null;
        });
      },

      // ---------- Výběr data ----------
      // v-calendar s `is-required` neposílá "input", když zákazník klikne na den,
      // který je už vybraný (např. dnešek při otevření, nebo den vyzvednutí u data vrácení).
      // Proto poslouchá i "dayclick", který přijde vždy.
      isDayClickable(day) {
        if (!day || !day.date || day.isDisabled) return false;
        if (this.beginOfDay(day.date) < this.beginOfDay(new Date())) return false;
        const a = this.availability[this.getDate(day.date)];
        return a !== 'closed' && a !== 'booked';
      },
      onStartDayClick(day) {
        if (this.isDayClickable(day)) this.onStartDatePicked(day.date);
      },
      onEndDayClick(day) {
        if (!this.isDayClickable(day)) return;
        if (this.range.start && this.beginOfDay(day.date) < this.range.start) return;
        if (this.nextBlockedAfterStart && this.beginOfDay(day.date) > this.beginOfDay(this.nextBlockedAfterStart)) return;
        this.onEndDatePicked(day.date);
      },
      onStartDatePicked(date) {
        if (!date) return;
        const day = this.beginOfDay(date);
        const changed = !this.range.start || this.range.start.getTime() !== day.getTime();
        this.range.start = day;
        this.startDateProxy = day;
        if (changed) this.resetAfter('startDate');
        this.advanceFrom('startDate');
      },
      onEndDatePicked(date) {
        if (!date) return;
        const day = this.beginOfDay(date);
        const changed = !this.range.end || this.range.end.getTime() !== day.getTime();
        this.range.end = day;
        this.endDateProxy = day;
        if (changed) this.resetAfter('endDate');
        this.advanceFrom('endDate');
      },

      // ---------- Výběr času ----------
      selectedTime(field) {
        return field === 'startTime' ? this.fromTime : this.toTime;
      },
      onTimePicked(field, time) {
        if (field === 'startTime') {
          const changed = this.fromTime !== time;
          this.fromTime = time;
          if (changed) this.resetAfter('startTime');
        } else {
          this.toTime = time;
        }
        this.advanceFrom(field);
      },
      slotsFor(field) {
        // Vrátí [{time, available}] pro všechny sloty daného dne (obsazené zůstávají viditelné, ale zakázané)
        const date = field === 'startTime' ? this.range.start : this.range.end;
        if (!date) return [];
        const dayAvail = this.availability[this.getDate(date)];
        if (!dayAvail || typeof dayAvail !== 'object') return [];

        return Object.keys(dayAvail)
          // zavřené sloty (dočasná uzavírka) vůbec nenabízíme
          .filter((time) => time !== '00:00' && dayAvail[time] !== 'closed')
          .sort((a, b) => this.minutes(a) - this.minutes(b))
          .map((time) => {
            let available = dayAvail[time] === 'available';
            if (available && field === 'endTime' && this.startDateTime) {
              const candidate = this.setDayTime(date, time);
              const hours = (candidate.getTime() - this.startDateTime.getTime()) / 3600000;
              const minHours = this.minimumRental && this.minimumRental > 0 ? this.minimumRental : 0.5;
              if (hours < minHours) available = false;
              if (this.nextBlockedAfterStart && candidate >= this.nextBlockedAfterStart) available = false;
            }
            return { time, available };
          });
      },
      timeGroups(field) {
        const slots = this.slotsFor(field);
        return [
          { key: 'morning',   label: this.t('marketplace.calendar.morning', 'Morning'),     slots: slots.filter((s) => this.minutes(s.time) < 12 * 60) },
          { key: 'afternoon', label: this.t('marketplace.calendar.afternoon', 'Afternoon'), slots: slots.filter((s) => this.minutes(s.time) >= 12 * 60 && this.minutes(s.time) < 17 * 60) },
          { key: 'evening',   label: this.t('marketplace.calendar.evening', 'Evening'),     slots: slots.filter((s) => this.minutes(s.time) >= 17 * 60) },
        ];
      },
      hasAnySlot(field) {
        return this.slotsFor(field).some((s) => s.available);
      },

      // ---------- Dostupnost ----------
      onCalendarPageChange(page) {
        if (!page) return;
        const key = `${page.year}-${page.month}`;
        if (this.loadedMonths.has(key)) return;
        this.loadedMonths.add(key);
        this.getAvailability(page.month, page.year);
      },
      getAvailability(month, year) {
        fetch(this.availabilityUrl + '?month=' + month + '&year=' + year)
          .then((response) => response.json())
          .then((data) => {
            this.availability = Object.assign({}, this.availability, data);
            this.updateDisabledDates();
          });
      },
      firstAvailableDate() {
        const today = this.beginOfDay(new Date());
        const key = Object.keys(this.availability)
          .filter((x) => this.availability[x] !== 'closed' && this.availability[x] !== 'booked' && this.beginOfDay(new Date(x)) >= today)
          .sort((a, b) => new Date(a) - new Date(b))[0];
        return key ? this.beginOfDay(new Date(key)) : null;
      },
      updateDisabledDates() {
        const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
        const disabled = [{ start: null, end: yesterday }];
        const openDays = [], closedDays = [], partialDays = [];

        for (const date in this.availability) {
          if (new Date(date) <= yesterday) continue;
          const day = this.availability[date];
          if (day === 'booked') {
            disabled.push(new Date(date));
          } else if (day === 'closed') {
            closedDays.push(date);
            disabled.push(new Date(date));
          } else if (Object.values(day).some((x) => x === 'booked')) {
            partialDays.push(date);
          } else {
            openDays.push(date);
          }
        }

        this.disabledDates = disabled;
        this.attributes[0].dates = openDays;
        this.attributes[1].dates = closedDays;
        this.attributes[2].dates = disabled;
        this.attributes[3].dates = partialDays;
        this.attributes = [...this.attributes];
      },

      // ---------- Emit do rodiče (stejný kontrakt jako dřív) ----------
      emitPeriod() {
        if (this.validPeriod) {
          this.$emit('updateDates', this.bookPeriod);
        } else {
          this.$emit('updateDates', { from: null, to: null });
        }
      },

      // ---------- Datumové helpery ----------
      parseIncoming(value) {
        // Přijde "2026-10-15T09:00:00" nebo "2026/10/15 09:00:00" podle toho,
        // odkud se to bere. Zajímá nás jen den, čas zahazujeme.
        if (!value) return null;
        const d = new Date(String(value).replace(/\//g, '-').replace(' ', 'T'));
        if (isNaN(d.getTime())) return null;
        const day = this.beginOfDay(d);
        return day < this.beginOfDay(new Date()) ? null : day;
      },
      beginOfDay(date) {
        if (!date) return null;
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      },
      setDayTime(date, hm) {
        if (!date || !hm) return null;
        const [h, m] = hm.split(':');
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), Number(h), Number(m));
      },
      minutes(hm) {
        const [h, m] = hm.split(':').map(Number);
        return h * 60 + m;
      },
      getDate(date) {
        if (!date) return null;
        return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
      },
      getDateWZero(date) {
        if (!date) return null;
        return `${date.getFullYear()}/${this.addZero(date.getMonth() + 1)}/${this.addZero(date.getDate())}`;
      },
      addZero(s) { return s < 10 ? '0' + s : s; },
      diffHours(a, b) { return Math.abs((b.getTime() - a.getTime()) / 3600000); },
    },
  }
</script>
